import React from 'react'

import { Box, Divider, Grid, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'

/**
 * Loading placeholder for the tag Shop grid (§19 — a skeleton fills the SAME space the
 * loaded content will occupy; never a bare spinner, which this replaced).
 *
 * It mirrors `TagsShopProductPreview` region for region rather than dropping a generic
 * block in the middle of the page: the same `Grid container spacing={4}` with the same
 * `xs/sm/md/lg` breakpoints, the same `Card` shell, the same padded square image block,
 * the divider, and then each labelled field of the card body — title + price, the
 * two-line description, Capabilities, Sizes/Range, and the Best-for chip zone.
 *
 * WHY MIRROR THE STRUCTURE RATHER THAN HARD-CODE A CARD HEIGHT. The card's height is a
 * sum of its regions, and several of those regions wrap with the column width (the
 * capability badges and the Best-for chips both reflow at `md`/`lg`). Restating the
 * regions with the same wrappers lets the skeleton wrap the same way the real card does
 * at every breakpoint, so parity survives a resize instead of holding only at the width
 * it was measured at.
 *
 * The pixel numbers below are the loaded card's, measured in the browser: title row 36,
 * description 42 (`3em` at body3's 14px/1.5), each field label 18 with an 8px gap to its
 * value, the Sizes/Range values 18, the badges 34, and the Best-for zone a fixed 56
 * (`BEST_FOR_ROWS * CHIP_ROW_PITCH`). Every one of those is set by type metrics or a
 * constant, so they do not move with the viewport — checked against the rendered card at
 * both 1830x1304 (4-up) and 1100x800 (3-up), where card box, region heights and region
 * offsets all matched to 0.0px.
 *
 * The chip and badge widths are the rendered ones for a representative product. They only
 * decide where a row breaks, which a loading state cannot know for real labels it has not
 * fetched yet — and a card whose product carries more badge rows than this reserves will
 * be taller. That is a difference INSIDE the pane, not a page reflow: the pane's own
 * height comes from `useFillBottom` measuring its top, which no amount of content below
 * can move.
 */

// Two full rows at the widest breakpoint. The scroll container shows roughly one row of
// a ~677px card, so this covers the visible area plus the overscroll the bottom fade
// hints at, without mounting a screenful of invisible bars.
const CARD_COUNT = 8

// Representative rendered widths (px) for the capability badges and the Best-for chips.
// Real labels vary; these are what a mid-ladder tag product measures.
const BADGE_WIDTHS = [102, 86, 95]
const CHIP_WIDTHS = [85, 95, 108, 122, 56]

/**
 * One line of text. The BOX is the footprint the rendered `Typography` occupies (18px for
 * body3, 36 for the stretched title); the bar inside is drawn thinner and centred in it,
 * the way a glyph sits inside its line box. Sizing the bar itself to the line height
 * instead would look like a solid slab AND come up short, because a bare `Skeleton`
 * inside a `Stack` has its own margins overridden by the Stack's spacing rule.
 */
const TextBar = ({ width, box = 18, bar = 12 }: { width: number | string; box?: number; bar?: number }) => (
  <Box sx={{ height: box, display: 'flex', alignItems: 'center' }}>
    <Skeleton width={width} height={bar} />
  </Box>
)

const SkeletonCard = () => (
  <Card sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
    {/* Image block — same padding and same square, width-capped box as the real card,
        so the artwork's footprint matches at every column width. */}
    <Box sx={{ pt: 6, pb: 4, px: 6, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 260, aspectRatio: '1 / 1' }}>
        <Skeleton variant="rounded" width="100%" height="100%" sx={{ borderRadius: 3 }} />
      </Box>
    </Box>
    <Divider />
    <Stack spacing={4} sx={{ pt: 4, pb: 6, px: 6, flex: 1 }}>
      {/* Title (body1) on the left, the two-line price block right-aligned. */}
      <Stack direction="row" justifyContent="space-between" spacing={4} sx={{ height: 36 }}>
        <TextBar width={129} box={36} bar={14} />
        <Stack alignItems="flex-end" spacing={0}>
          <TextBar width={40} />
          <TextBar width={82} />
        </Stack>
      </Stack>

      {/* Description — a fixed two-line block on the real card, so it reserves the same
          42px here whether or not the copy would have filled both lines. */}
      <Box sx={{ height: 42, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: '3px' }}>
        <Skeleton width="100%" height={12} />
        <Skeleton width="72%" height={12} />
      </Box>

      {/* Capabilities — label over a wrapping badge row. */}
      <Stack spacing={2}>
        <TextBar width={78} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {BADGE_WIDTHS.map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={34} sx={{ borderRadius: 45 }} />
          ))}
        </Box>
      </Stack>

      {/* Sizes + Range — two labelled fields side by side. */}
      <Stack direction="row" spacing={6} sx={{ height: 44 }}>
        <Stack spacing={2}>
          <TextBar width={44} />
          <TextBar width={63} />
        </Stack>
        <Stack spacing={2}>
          <TextBar width={44} />
          <TextBar width={39} />
        </Stack>
      </Stack>

      {/* Best for — label over the fixed-height, clipped chip zone. `overflow: hidden`
          and the 56px height are the real card's, which is what keeps every card in a
          row the same height however many categories a product carries. */}
      <Stack spacing={2}>
        <TextBar width={54} />
        <Box
          sx={{
            height: 56,
            overflow: 'hidden',
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'flex-start',
          }}
        >
          {CHIP_WIDTHS.map((w, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={w}
              height={20}
              sx={{ mr: 2, mt: 2, borderRadius: 45 }}
            />
          ))}
        </Box>
      </Stack>
    </Stack>
  </Card>
)

export const ShopSkeleton = () => (
  <Grid container spacing={4}>
    {Array.from({ length: CARD_COUNT }).map((_, i) => (
      // Same breakpoints and the same `minHeight` floor as the loaded grid item, so the
      // columns and the row pitch are identical by construction rather than by two sets
      // of numbers agreeing.
      <Grid item xs={12} sm={6} md={4} lg={3} key={i} sx={{ minHeight: 350 }}>
        <SkeletonCard />
      </Grid>
    ))}
  </Grid>
)
